'use client';

import { useLoader } from '@react-three/fiber';
import { GLTFLoader, DRACOLoader } from 'three-stdlib';
import type { GLTF } from 'three-stdlib';

// Client-side loader for protected models.
//
// Models are served obfuscated (see scripts/protect-models.mjs) so that a direct
// download of the file is not a valid GLB. Here we fetch the bytes, undo the
// fixed-key XOR, and hand a real GLB buffer to GLTFLoader.parse().
//
// Like any client-rendered asset, the browser must receive the bytes to display
// the model, so this is deterrence against casual/shallow download, not DRM.

// Shared, fixed 32-byte key (hex). Keep in sync with scripts/protect-models.mjs
const KEY_HEX = '7f3a9c21e8b4d6051a7c9f0e2b4d6a8c3e5f7a0b1c2d3e4f5a6b7c8d9e0f1a2b';
const DRACO_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';

function keyBytes(): Uint8Array {
  const out = new Uint8Array(KEY_HEX.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(KEY_HEX.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function deobfuscate(input: Uint8Array): Uint8Array {
  const key = keyBytes();
  const out = new Uint8Array(input.length);
  for (let i = 0; i < input.length; i++) out[i] = input[i] ^ key[i % key.length];
  return out;
}

function configureDraco(loader: GLTFLoader): void {
  const draco = new DRACOLoader();
  draco.setDecoderPath(DRACO_PATH);
  loader.setDRACOLoader(draco);
}

export class ProtectedGLTFLoader extends GLTFLoader {
  load(
    url: string,
    onLoad: (gltf: GLTF) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void
  ): void {
    if (!url) {
      onError?.(new ErrorEvent('error', { message: 'Empty model URL' }));
      return;
    }
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buf) => deobfuscate(new Uint8Array(buf)))
      .then(
        (bytes) =>
          new Promise<GLTF>((resolve, reject) => {
            // buffer is a fresh ArrayBuffer here (res.arrayBuffer()), so its .buffer
            // reference is exactly the GLB. DRACO decoding is handled by GLTFLoader
            // via the DRACOLoader configured in configureDraco.
            this.parse(bytes.buffer as ArrayBuffer, '', resolve, reject);
          })
      )
      .then((gltf) => onLoad(gltf))
      .catch((err) => onError?.(err as ErrorEvent));

    // The remote file is single-shot (no progress), signal completion immediately.
    onProgress?.(new ProgressEvent('progress', { lengthComputable: true, loaded: 1, total: 1 }));
  }
}

export function useProtectedGLTF(url: string): GLTF {
  return useLoader(ProtectedGLTFLoader, url, configureDraco);
}

useProtectedGLTF.preload = (url: string): void => {
  useLoader.preload(ProtectedGLTFLoader, url, configureDraco);
};

useProtectedGLTF.clear = useLoader.clear;